import { GoogleMap, StreetViewPanorama } from '@react-google-maps/api';
import { useAppSelector } from 'app/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type GoogleMapViewProps = {
  size: 'normal' | 'small';
};

export default function GoogleMapView({ size = 'normal' }: GoogleMapViewProps) {
  const { propertyLocation, attomData } = useAppSelector(
    (state) => state.project
  );
  const { googleApiLoaded } = useAppSelector((state) => state.ui);
  const containerStyle = useMemo(() => {
    return {
      height: size === 'normal' ? '500px' : '300px',
      width: '100%'
    } as const;
  }, [size]);
  const attomCenter = useMemo(
    () =>
      attomData
        ? {
            lat: Number(attomData.location.latitude),
            lng: Number(attomData.location.longitude)
          }
        : null,
    [attomData?.location?.latitude, attomData?.location?.longitude]
  );
  const resolvedCenter: google.maps.LatLngLiteral | null = useMemo(
    () => propertyLocation ?? attomCenter ?? null,
    [propertyLocation, attomCenter]
  );

  const mapCenter: google.maps.LatLngLiteral = useMemo(
    () => resolvedCenter ?? ({ lat: 0, lng: 0 } as google.maps.LatLngLiteral),
    [resolvedCenter]
  );
  const mapOptions: google.maps.MapOptions = useMemo(
    () => ({
      center: mapCenter,
      streetViewControl: false,
      backgroundColor: 'lightgray'
    }),
    [mapCenter]
  );
  const mapZoom = 16;
  const [panoOptions, setPanoOptions] =
    useState<google.maps.StreetViewPanoramaOptions | null>(null);
  const prevCenterRef = useRef<google.maps.LatLngLiteral | null>(null);

  const initializeStreetView = useCallback(() => {
    if (!window.google || !resolvedCenter) {
      return;
    }

    try {
      const streetViewService = new window.google.maps.StreetViewService();
      const latLng = new window.google.maps.LatLng(
        resolvedCenter.lat,
        resolvedCenter.lng
      );

      streetViewService.getPanorama(
        { location: latLng, radius: 50 },
        (data, status) => {
          if (
            status === window.google.maps.StreetViewStatus.OK &&
            data &&
            data.location &&
            data.location.latLng
          ) {
            const panoLatLng = data.location.latLng;
            const targetLatLng = new window.google.maps.LatLng(
              resolvedCenter.lat,
              resolvedCenter.lng
            );
            let heading = 0;

            if (window.google?.maps?.geometry?.spherical?.computeHeading) {
              heading = window.google.maps.geometry.spherical.computeHeading(
                panoLatLng,
                targetLatLng
              );
            } else {
              const from = panoLatLng.toJSON();
              const to = targetLatLng.toJSON();
              const phi1 = (from.lat * Math.PI) / 180;
              const phi2 = (to.lat * Math.PI) / 180;
              const deltaLambda = ((to.lng - from.lng) * Math.PI) / 180;
              const y = Math.sin(deltaLambda) * Math.cos(phi2);
              const x =
                Math.cos(phi1) * Math.sin(phi2) -
                Math.sin(phi1) * Math.cos(phi2) * Math.cos(deltaLambda);
              const theta = Math.atan2(y, x);

              heading = ((theta * 180) / Math.PI + 360) % 360;
            }

            setPanoOptions({
              position: data.location.latLng.toJSON(),
              pov: { heading, pitch: 0 },
              zoom: 1.5,
              visible: true
            });
          } else {
            setPanoOptions(null);
          }
        }
      );
    } catch {
      setPanoOptions(null);
    }
  }, [resolvedCenter]);

  useEffect(() => {
    if (!googleApiLoaded) {
      return;
    }

    if (!resolvedCenter) {
      setPanoOptions(null);
      prevCenterRef.current = null;
      return;
    }

    const prev = prevCenterRef.current;
    const hasChanged =
      !prev ||
      prev.lat !== resolvedCenter.lat ||
      prev.lng !== resolvedCenter.lng;

    if (hasChanged) {
      prevCenterRef.current = resolvedCenter;
      initializeStreetView();
    }
  }, [initializeStreetView, googleApiLoaded, resolvedCenter]);

  if (!googleApiLoaded || !resolvedCenter) {
    return <div style={containerStyle} />;
  }

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={mapCenter}
      zoom={mapZoom}
      options={mapOptions}
    >
      {panoOptions && <StreetViewPanorama options={panoOptions} />}
    </GoogleMap>
  );
}
